import { FC, ReactNode, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SearchContextProvider, useSearchContext } from "../../../../../context/searchContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useBorderSystemsApi } from "../../../../../http/border/useBorderSystems";
import { CustomError } from "../../../../../http/errors";
import FlexContent from "../../../../ui/FlexContent";
import Header, { HeaderPath } from "../../../../ui/Header";
import Tabs, { Tab } from "../../../../ui/Tabs";
import { BORDER } from "../../AdminSystemSettings";
import FasteningChoices from "../fasteningChoices/FasteningChoices";
import SystemNormatives from "../systemNormatives/SystemNormatives";
import BorderSystemDetails from "./SystemDetails";
import SystemUprights from "./SystemUprights";

const BorderSystem: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { borderSystemId } = useParams<{ borderSystemId: string }>();
  const systemsApi = useBorderSystemsApi();
  const [searchParams] = useSearchParams();
  const selectedIndex = Number(searchParams.get("tab")) || 0;

  const aborter = useRef(new AbortController());
  const { error, data, isMutating, trigger } = systemsApi.getAdminBorderSystem(borderSystemId!, aborter.current.signal);

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: BORDER },
    {
      url: `/admin/systems/${systemId}/settings?tab=0`,
      display: `${i18n.translation.common.settings}: ${i18n.translation.systems.plural}`,
    },
    { display: data?.name ?? "" },
  ];

  useEffect(() => {
    borderSystemId && trigger();
  }, [borderSystemId]);

  const tabs: Tab[] = [
    { title: i18n.translation.common.generalData },
    { title: i18n.translation.normatives.plural },
    { title: i18n.translation.fasteningChoices.plural },
    // { title: i18n.translation.gates.plural },
    // { title: i18n.translation.membraneProtections.plural },
    { title: i18n.translation.uprights.plural, hasSubTabs: true },
  ];

  const contents: ReactNode[] = [
    <FlexContent padding direction="row">
      <BorderSystemDetails
        borderSystem={!error ? data : undefined}
        disabled={isMutating || !!error}
        isLoading={!data}
      />
    </FlexContent>,
    <SystemNormatives active={selectedIndex === 1} />,
    <FasteningChoices active={selectedIndex === 2} />,
    // <SystemGates />,
    // <SystemMembraneProtections />,
    <SystemUprights active={selectedIndex === 3} />,
  ];

  return (
    <SearchContextProvider>
      <BorderSystemContent
        title={`${i18n.translation.systems.singular}: ${data?.name ?? ""}`}
        error={error}
        path={path}
        tabs={tabs}
        contents={contents}
      />
    </SearchContextProvider>
  );
};

type BorderSystemContentProps = {
  title: string;
  error?: CustomError;
  path: HeaderPath[];
  tabs: Tab[];
  contents: ReactNode[];
};

const BorderSystemContent: FC<BorderSystemContentProps> = ({ title, error, path, tabs, contents }) => {
  const { actions, title: tabTitle } = useSearchContext();

  return (
    <>
      <Header title={tabTitle ? `${title} · ${tabTitle}` : title} error={error} path={path} buttons={actions} />
      <Tabs urlParameter="tab" tabs={tabs} contents={contents} />
    </>
  );
};

export default BorderSystem;
